import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Mail, MapPin, Clock } from "lucide-react";

export default function Footer() {
  const { t } = useLanguage();

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const links = [
    { label: t.nav.services, id: "services" },
    { label: t.nav.serviceArea, id: "service-area" },
    { label: t.nav.testimonials, id: "testimonials" },
    { label: t.nav.about, id: "about" },
    { label: t.nav.contact, id: "contact" },
  ];

  return (
    <footer data-testid="footer" className="bg-[#0B0A08] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-20 lg:py-32">
        <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2.5 mb-5">
              <div className="w-9 h-9 rounded-full bg-gradient-to-r from-[#BF953F] via-[#F9E58A] to-[#B38728] flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" className="w-4.5 h-4.5" stroke="#000" strokeWidth="2.5">
                  <path d="M12 2a4 4 0 0 1 4 4v2h-2V6a2 2 0 0 0-4 0v2H8V6a4 4 0 0 1 4-4z" />
                  <rect x="6" y="10" width="12" height="12" rx="1" />
                  <circle cx="12" cy="16" r="2" />
                </svg>
              </div>
              <span className="font-heading text-lg font-bold text-white">
                Houston <span className="text-gold-gradient">Locks</span>
              </span>
            </div>
            <p className="text-sm text-[#8A8881] leading-relaxed max-w-xs">
              {t.footer.tagline}
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-5">
              {t.footer.quickLinks}
            </h4>
            <ul className="space-y-3">
              {links.map((link) => (
                <li key={link.id}>
                  <button
                    data-testid={`footer-link-${link.id}`}
                    onClick={() => scrollTo(link.id)}
                    className="text-sm text-[#8A8881] hover:text-[#D4AF37] transition-colors duration-300"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37] mb-5">
              {t.footer.contactInfo}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-[#8A8881]">
                <Mail className="w-4 h-4 text-[#D4AF37]/60 flex-shrink-0" />
                <span>{t.footer.email}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#8A8881]">
                <MapPin className="w-4 h-4 text-[#D4AF37]/60 flex-shrink-0" />
                <span>{t.footer.address}</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-[#8A8881]">
                <Clock className="w-4 h-4 text-[#D4AF37]/60 flex-shrink-0" />
                <span>{t.footer.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-5 flex flex-col sm:flex-row justify-between items-center gap-2">
          <p className="text-xs text-[#8A8881]/60">
            &copy; {new Date().getFullYear()} Houston Pro Locksmith. {t.footer.rights}
          </p>
          <p className="text-xs text-[#8A8881]/60">Houston, Texas</p>
        </div>
      </div>
    </footer>
  );
}
